/**
 * Which SCLA application an account gets.
 * Keep in sync with txt-core-backend/src/utils/scla-application-kind.ts.
 *
 * txter: returner application, stored as appType "txter".
 * user, other, scla-applicant, and lil: regular application, stored as appType "scla-applicant".
 * Older regular applications may still say "lil". That is the same set as "scla-applicant".
 * alumni and any other account type: no application.
 */
export type SclaApplicationKind = 'returner' | 'regular';

export type SclaStoredAppType = 'txter' | 'scla-applicant';

export type SclaApplicationDecision = {
  allowed: boolean;
  kind: SclaApplicationKind | null;
  appType: SclaStoredAppType | null;
  label: string | null;
};

export type SclaApplicationStatusLabel =
  | 'not started'
  | 'in progress'
  | 'submitted';

const NOT_ALLOWED: SclaApplicationDecision = {
  allowed: false,
  kind: null,
  appType: null,
  label: null,
};

const RETURNER: SclaApplicationDecision = {
  allowed: true,
  kind: 'returner',
  appType: 'txter',
  label: 'Returner application',
};

const REGULAR: SclaApplicationDecision = {
  allowed: true,
  kind: 'regular',
  appType: 'scla-applicant',
  label: 'SCLA application',
};

const DECISION_BY_USER_TYPE: Record<string, SclaApplicationDecision> = {
  txter: RETURNER,
  user: REGULAR,
  other: REGULAR,
  'scla-applicant': REGULAR,
  lil: REGULAR,
};

export function isSclaReturnerAppType(appType: string | null | undefined): boolean {
  return appType === 'txter';
}

export function resolveSclaApplication(
  userType: string | null | undefined,
): SclaApplicationDecision {
  return (userType && DECISION_BY_USER_TYPE[userType]) || NOT_ALLOWED;
}

/** Submitted applications keep the type they were submitted with. Open ones follow the account. */
export function sclaShowsTxterBadge(
  application: {
    appType?: string | null;
    appStatus?: string | null;
    submissionTimestamp?: Date | string | null;
  } | null | undefined,
  userType: string | null | undefined,
): boolean {
  const submitted = Boolean(application?.submissionTimestamp)
    || application?.appStatus === 'complete';
  if (submitted) return isSclaReturnerAppType(application?.appType);
  return resolveSclaApplication(userType).kind === 'returner';
}

export function sclaApplicationStatusLabel(
  application: {
    appStatus?: string | null;
    submissionTimestamp?: Date | string | null;
  } | null,
): SclaApplicationStatusLabel {
  if (!application) return 'not started';

  const raw = (application.appStatus || '')
    .toLowerCase()
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const hasSubmission = Boolean(application.submissionTimestamp);

  // The API marks a submitted application as "complete".
  if (raw === 'submitted' || raw === 'complete' || hasSubmission) {
    return 'submitted';
  }
  if (raw === 'in progress') return 'in progress';
  return 'not started';
}
